import React, { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { withFormatter } from '@concur/nui-intl-runtime';
import {
    compose,
    isLocalizationModeEnabled,
    withLogger,
    withStylesLoaded,
} from '@concur/core-ui-shell';
import { isThemeSupported } from '@concur/ui-theming';
import Spinner from '@concur/nui-widgets/Spinner';
import Shellbar from './_Shellbar';
import {
    SERVICE_DESCRIPTION_GUIDE_ID,
    TRANSLATION_TOOL_URL_IDENTIFIER,
} from '../constants';
import FallbackComponent from '../FallbackComponent/_FallbackComponent';
import InlineError from '../FallbackComponent/_InlineError';
import withNuiIntl from '../utils/withNuiIntl';
import withStylesPlaceholder from '../utils/withStylesPlaceholder';
import withLoggerData from '../utils/withLoggerData';
import { getThemeCookie, removeThemeCookie } from '../utils/themeUtils';
import AppHeaderContext from './_AppHeaderContext';

const getProfileMenuItem = ({ formatter, isLoggedIn }) => (isLoggedIn ? {
    name: formatter.formattedMessage({ id: 'CoreUI.profile.profile' }),
    id: 'profile',
    url: '#', // too many variables to try and determine the proper profile settings page here
} : undefined
);

const getHelpMenuItem = ({ formatter, menuData }) => {
    const adjustedHelpMenuItem = menuData?.helpMenuItem || {
        name: formatter.formattedMessage({ id: 'CoreUI.help' }),
        id: 'help',
        url: '/',
    };

    // due to how language bundles load, we have have gotten the menu item, but
    // not gotten the text so let's go back and update that
    adjustedHelpMenuItem.name = formatter.formattedMessage({ id: 'CoreUI.help' });

    let helpSubMenuItems = adjustedHelpMenuItem.subItems || [];

    // remove Service Description Guide if it is present
    helpSubMenuItems = helpSubMenuItems.filter(
        (item) => item.id !== SERVICE_DESCRIPTION_GUIDE_ID,
    );

    adjustedHelpMenuItem.subItems = helpSubMenuItems.map((item) => (
        {
            ...item,
            anchorProps: {
                // format requested by analytics team
                'data-analytics-json': `{"applicationName":"helplinks","elementPath":"${item?.id}","action":"click","elementType":"link"}`,
            },
        }
    ));

    return adjustedHelpMenuItem;
};

const getMinimalMenuData = ({
    formatter, isLoggedIn, logger, isGov,
}) => {
    const correlationId = uuidv4();
    if (isLoggedIn) logger.error('Menu data is missing', { correlationId });

    return {
        helpMenuItem: getHelpMenuItem({ formatter, isGov }),
        homeMenuItem: {
            name: formatter.formattedMessage({ id: 'CoreUI.home' }),
            id: 'home',
            url: isLoggedIn ? '/home.asp' : undefined,
        },
        profileMenuItem: getProfileMenuItem({ formatter, isLoggedIn }),
        // mark this so we know this is "fake" menu data
        noMenuDataFallback: isLoggedIn ? (
            <InlineError correlationId={correlationId} />
        ) : undefined,
    };
};

const isMayFourth = () => {
    // May Fourth Easter Egg
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const currentDay = currentDate.getDate();

    // currentMonth is 0 indexed (Jan === 0)
    return currentMonth === 4 && currentDay === 4;
};

// in lieu of passing in user permissions, we can scan the help menu items
// for the existence of the changeLocalizationMode function call in the url
const isLocalizationUser = (helpMenuItem) => helpMenuItem?.subItems?.some(
    (item) => item.url?.indexOf(TRANSLATION_TOOL_URL_IDENTIFIER) >= 0,
);

const AppHeader = (props) => {
    const {
        employeeName,
        formatter,
        fullName,
        menuData,
        ...otherProps
    } = props;

    const [showSpinner, setShowSpinner] = useState(false);

    useEffect(() => {
        // cookie cleanup is predicated on being signed in
        if (!props?.isLoggedIn) return;

        const themeCookieValue = getThemeCookie();

        // make sure theme cookie (if present) is a valid theme for the branding id
        if (!!themeCookieValue && !isThemeSupported(props?.brandingId, themeCookieValue)) {
            // just remove the cookie and let other processes restore/fix it
            setShowSpinner(true);
            removeThemeCookie();
            setTimeout(() => {
                window?.location?.reload();
            }, 2000);
        }
    }, [props]);

    // This is a patch for nui apps that rely on Menu.cshtml urls like
    // eslint-disable-next-line
    // javascript:if (CNQR && CNQR.currentPage && (CNQR.currentPage === 'processor') && CNQR.processor && CNQR.processor.MenuLinkClicked) {CNQR.processor.MenuLinkClicked()} else {document.location.href='?RptListFilter=processor'}
    // which Error if CNQR is not defined
    // TODO: It'd be better to do these checks here:
    // https://github.concur.com/t1/travel/blob/master/Outtask%20Web%20Server/SharedMasterPages/Gateway/Menu.cshtml
    if (typeof CNQR === 'undefined') {
        window.CNQR = {};
    }

    if (menuData && !menuData.profileMenuItem) {
        menuData.profileMenuItem = getProfileMenuItem(props);
    }

    if (menuData) {
        menuData.helpMenuItem = getHelpMenuItem(props);
    }

    const adjustedProps = {
        ...otherProps,
        menuData: menuData || getMinimalMenuData(props),
    };

    const { firstName, lastName, preferredName } = employeeName || {};
    const contextValue = useMemo(() => ({
        isMayFourth: isMayFourth(),
        isLocalizationUser: isLocalizationUser(menuData?.helpMenuItem),
        isLocalizationModeEnabled: isLocalizationModeEnabled(),
        employeeDisplayName: formatter.formatEmployeeName({
            firstName,
            lastName,
            preferredName,
            fullName,
        }),
    }), [
        props.brandingId,
        props.themeName,
        menuData?.helpMenuItem,
        firstName,
        lastName,
        preferredName,
        fullName,
    ]);

    const fallbackComp = (errorId) => (
        <FallbackComponent
            componentCssBlock={Shellbar.cssBlock}
            correlationId={errorId}
        />
    );

    return (
        <AppHeaderContext.Provider value={contextValue}>
            <Shellbar
                {...adjustedProps}
                errorComp={fallbackComp}
            />
            <Spinner
                message={formatter.formattedMessage({ id: 'CoreUI.refreshTheme' })}
                type="fullScreen"
                visible={showSpinner}
            />
        </AppHeaderContext.Provider>
    );
};

AppHeader.displayName = 'AppHeader';

AppHeader.propTypes = {
    ...Shellbar.propTypes,
};

export default compose(
    withStylesPlaceholder,
    withStylesLoaded,
    withNuiIntl,
    withFormatter,
    withLoggerData,
    withLogger,
)(AppHeader);
